import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Table, Button, message, Input } from "antd";
import { useGlobal } from "../global/user";
import { User, Companies } from "../api";
import { UserFieldModal, UserModal } from "./modal";

const LEVEL_OPTIONS = [
  { value: 1, text: "唯讀" },
  { value: 2, text: "可控制" },
  { value: 3, text: "管理員" },
  { value: 4, text: "網管" },
];

const LEVEL_DATA = {
  1: "唯讀",
  2: "可控制",
  3: "管理員",
  4: "網管",
};

export default function UserTable() {
  const { user } = useGlobal();
  const [data, setData] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [modalState, setModalState] = useState({
    editAccess: false,
    editUser: false,
  });
  const [targetUser, setTargetUser] = useState(null);
  const [targetUserId, setTargetUserId] = useState(-1);

  // 獲取使用者數據
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await User.GetUsers(user.class);
      // class 4 (網管) 看到所有用戶，class 3 (管理員) 看到同公司的所有用戶
      console.log("Fetched users:", response.data);
      console.log("Current user class:", user.class);
      console.log("Current user company ID:", user.company_id);
      const filteredData = response.data.filter((data) => 
        user.class === 4 || (user.class === 3 && data.company_id === user.company_id)
      );
      setData(filteredData);
    } catch (error) {
      message.error("獲取使用者數據失敗");
      console.error("Failed to fetch users:", error);
    } finally {
      setLoading(false);
    }
  }, [user.class, user.company_id]);

  // 獲取公司數據
  const fetchCompanies = useCallback(async () => {
    try {
      const response = await Companies.getAllCompanies();
      // 過濾無效數據
      const validCompanies = response.filter(c => c && c.id && c.company_name);
      setCompanies(validCompanies);
    } catch (error) {
      message.error("獲取公司數據失敗");
      console.error("Failed to fetch companies:", error);
    }
  }, []);

  useEffect(() => {
    if (user.class) {
      fetchUsers();
      fetchCompanies();
    }
  }, [user.class, fetchUsers, fetchCompanies]);

  // 根據公司 ID 獲取公司名稱
  const getCompanyName = (companyId) => {
    const company = companies.find(c => c && c.id === companyId);
    return company ? company.company_name : '未知公司';
  };

  const filteredData = useMemo(() => {
    if (!searchText) {
      return data;
    }

    const normalizedSearch = searchText.trim().toLowerCase();

    return data.filter((record) => {
      const companyName = getCompanyName(record.company_id).toLowerCase();
      return (
        (record.name || "").toLowerCase().includes(normalizedSearch) ||
        companyName.includes(normalizedSearch)
      );
    });
  }, [data, companies, searchText]);

  // 表格列定義
  const columns = [
    {
      title: "使用者名稱",
      dataIndex: "name",
      key: "name"
    },
    {
      title: "信箱",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "工作單位",
      dataIndex: "company_id",
      key: "company",
      render: (companyId) => getCompanyName(companyId),
      minWidth: 100
    },
    {
      title: "註冊時間",
      dataIndex: "register_time",
      key: "register_time",
      align: "center",
      minWidth: 300
    },
    {
      title: "權限設定",
      dataIndex: "class",
      filters: LEVEL_OPTIONS,
      onFilter: (value, record) => record.class === value,
      render: (level) => LEVEL_DATA[level],
      align: "center",
      minWidth: 115
    },
    {
      title: "場域權限",
      key: "fieldAccess",
      render: (_, record) => (
        <Button
          onClick={() => {
            setTargetUserId(record.id);
            setModalState((prev) => ({ ...prev, editAccess: true }));
          }}
        >
          權限設定
        </Button>
      ),
      align: "center",
      minWidth: 140
    },
    {
      title: "編輯使用者",
      key: "edit",
      render: (_, record) => (
        <Button
          onClick={() => {
            setTargetUser(record);
            setModalState((prev) => ({ ...prev, editUser: true }));
          }}
        >
          編輯使用者
        </Button>
      ),
      align: "center",
      minWidth: 150
    },
  ];

  // 處理模態框的開關
  const handleModalClose = (modalType) => {
    setModalState((prev) => ({ ...prev, [modalType]: false }));
    if (modalType === "editUser") {
      setTargetUser(null);
    } else if (modalType === "editAccess") {
      setTargetUserId(-1);
    }
  };

  // 處理使用者數據更新
  const handleUserUpdate = useCallback((updatedData) => {
    setData(updatedData);
  }, []);

  return (
    <React.Fragment>
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="搜尋使用者名稱或工作單位"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={(value) => setSearchText(value)}
          style={{ width: 320, minWidth: 240 }}
        />

        <Button
          className="mb-2"
          style={{
            float: "left",
            marginBottom: 20,
          }}
          onClick={() => {
            setTargetUser(null);
            setModalState((prev) => ({ ...prev, editUser: true }));
          }}
        >
          新增使用者
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        rowKey="id"
        pagination={{
          defaultPageSize: 10,
          showTotal: (total) => `共 ${total} 筆資料`,
        }}
        scroll={{ x: "max-content" }}
        bordered
      />

      <UserModal
        visible={modalState.editUser}
        setVisible={(visible) => setModalState((prev) => ({ ...prev, editUser: visible }))}
        setUsers={handleUserUpdate}
        value={targetUser}
        companies={companies}
        setCompanies={setCompanies}
        onClose={() => handleModalClose("editUser")}
        refreshCompanies={fetchCompanies}
      />

      <UserFieldModal
        visible={modalState.editAccess}
        setVisible={(visible) => setModalState((prev) => ({ ...prev, editAccess: visible }))}
        userID={targetUserId}
        onClose={() => handleModalClose("editAccess")}
      />
    </React.Fragment>
  );
}
